import { useReadQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Grid } from "@mui/system";
import DonutChart from "components/Dashboard/DonutChart";
import { TruncateTypography } from "components/TruncateTypography";
import { useMemo, useState } from "react";

import { useWasteOverviewState } from "../../WasteOverviewContext";
import { managementMethodColors, smallDonutProps } from "./constants";

const INITIAL_VISIBLE_ITEMS = 8;

export const ManagementMethodChart = () => {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);
  const [selectedManagementMethod, setSelectedManagementMethod] = useState<number | null>(null);

  const { managementMethodQuery } = useWasteOverviewState();
  const { data } = useReadQuery(managementMethodQuery);

  const seriesData = useMemo(() => {
    if (!data?.wasteManagementMethod || data.wasteManagementMethod.length === 0)
      return [
        {
          id: "no-management-methods",
          value: 1,
          color: grey[300],
          managementMethodCode: null,
          managementMethodDescription: "No Management Methods to show",
          totalQuantityPounds: 0,
        },
      ];

    return data.wasteManagementMethod.map((item, i) => ({
      ...item,
      id: item.managementMethodCode,
      value: item.totalQuantityPounds,
      label: item.managementMethodDescription,
      color: managementMethodColors[i % managementMethodColors.length],
    }));
  }, [data]);

  const totalPounds = useMemo(() => {
    return seriesData.reduce((acc, item) => acc + item.value, 0);
  }, [seriesData]);

  const legendItems = useMemo(() => {
    if (seriesData.length === 1 && seriesData[0].managementMethodCode === null) {
      return [
        {
          id: "no-management-methods",
          value: "💥 No Management Methods to show",
          color: "white",
        },
      ];
    }
    return seriesData.map(({ id, color, ...item }, index) => {
      const percentage = Math.round((item.totalQuantityPounds / totalPounds) * 100);
      const displayPercentage = percentage > 0 ? percentage : "<1";

      const isSelected = selectedManagementMethod === null || selectedManagementMethod === index;
      return {
        id,
        color: color,
        tooltip: item.managementMethodDescription,
        key: item.managementMethodCode + item.managementMethodDescription,
        value: (
          <Grid container width="100%" sx={{ opacity: isSelected ? 1 : 0.3 }}>
            <Grid size={{ xl: 6, lg: 8, xs: 5 }}>
              <TruncateTypography variant="body2">
                {item.managementMethodDescription}
              </TruncateTypography>
            </Grid>
            <Grid size={{ lg: 1, xs: 2 }}>
              <TruncateTypography variant="body2">{item.managementMethodCode}</TruncateTypography>
            </Grid>
            <Grid size={{ xl: 2, lg: 3, xs: 5 }} textAlign="right">
              <Typography variant="body2">
                {Math.round(item.totalQuantityPounds).toLocaleString()} lbs{" "}
                {item.totalQuantityPounds !== 0 ? `(${displayPercentage}%)` : ""}
              </Typography>
            </Grid>
          </Grid>
        ),
      };
    });
  }, [seriesData, selectedManagementMethod, totalPounds]);

  const displayLegendItems = useMemo(() => {
    if (showAll) return legendItems;

    const topLegendItems = legendItems.slice(0, INITIAL_VISIBLE_ITEMS);

    if (
      !showAll &&
      selectedManagementMethod !== null &&
      selectedManagementMethod >= INITIAL_VISIBLE_ITEMS
    ) {
      const selectedItem = legendItems[selectedManagementMethod];
      return [...topLegendItems, selectedItem];
    }

    return topLegendItems;
  }, [legendItems, showAll, selectedManagementMethod]);

  const shouldShowExpandButton = seriesData.length > INITIAL_VISIBLE_ITEMS;

  const isLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const isEmpty = seriesData.length === 1 && seriesData[0].managementMethodCode === null;

  return (
    <DonutChart
      {...smallDonutProps}
      seriesData={seriesData}
      legendItems={displayLegendItems}
      legend={{
        position: isLg ? (isMd ? "right" : "below") : "right",
        direction: "column",
        showExpandButton: shouldShowExpandButton,
        onExpandButtonClick: () => setShowAll(!showAll),
        expandButtonText: showAll
          ? `Collapse to Top ${INITIAL_VISIBLE_ITEMS} Management Methods`
          : "Show All Management Methods",
        expandButtonIcon: showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />,
      }}
      legendSx={{ width: "100%" }}
      onItemClick={(_, { dataIndex }) => setSelectedManagementMethod(dataIndex)}
      onLegendItemClick={({ id }) => {
        const index = displayLegendItems.findIndex((item) => item.id === id);
        // if the index is already selected, set it to null
        if (selectedManagementMethod === index || index === -1) {
          setSelectedManagementMethod(null);
        } else {
          setSelectedManagementMethod(index);
        }
      }}
      selectedIndex={selectedManagementMethod}
      justifyContent={isSm ? "center" : "start"}
      alignItems={isEmpty || isSm ? "center" : "start"}
    />
  );
};
